import { styled } from '@linaria/react';
import { Check, X as XIcon } from 'lucide-react';

import { WarningAside } from '@/components/Aside';

interface Props {
  isSuccessful: boolean;
  children: React.ReactNode;
}

function SuccessFailIndicator({ isSuccessful, children }: Props) {
  const Icon = isSuccessful ? Check : XIcon;
  return (
    <Wrapper data-success={isSuccessful ? 'true' : 'false'}>
      <IconWrapper>
        <Icon size={20} strokeWidth={3} />
      </IconWrapper>
      <p>{children}</p>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: fit-content;
  margin-inline: auto;
  padding: 8px;
  padding-right: 16px;
  font-size: 1rem;
  font-weight: var(--font-weight-medium);
  border-radius: 1000px;
  corner-shape: squircle;
  transition:
    color var(--color-swap-duration),
    background var(--color-swap-duration),
    border-color var(--color-swap-duration);

  html[data-color-mode='light'] & {
    --success-bg: var(--color-success-100);
    --success-border: var(--color-success-200);
    --success-icon-bg: var(--color-success-200);
    --success-txt: var(--color-success-500);
    --error-bg: var(--color-error-100);
    /*
      HACK: there's too big a jump between error-100 and error-200. Adding my own impromptu error-150 here:
    */
    --error-border: hsl(340deg 70% 90%);
    --error-icon-bg: hsl(340deg 70% 90%);
    --error-txt: var(--color-error-500);
  }
  html[data-color-mode='dark'] & {
    --success-bg: var(--color-success-200);
    --success-border: var(--color-success-300);
    --success-icon-bg: var(--color-success-300);
    --success-txt: var(--color-success-700);
    --error-bg: var(--color-error-200);
    --error-border: var(--color-error-300);
    --error-icon-bg: var(--color-error-300);
    --error-txt: var(--color-error-700);
  }

  html[data-color-mode='light'] ${WarningAside} & {
    --success-bg: white;
    --success-border: var(--color-warning-400);
    --error-bg: white;
    --error-border: var(--color-warning-400);
  }
  html[data-color-mode='dark'] ${WarningAside} & {
    --success-bg: var(--color-background);
    --success-border: var(--color-warning-400);
    --error-bg: var(--color-background);
    --error-border: var(--color-warning-400);
  }

  &[data-success='true'] {
    background: var(--success-bg);
    border: 1px solid var(--success-border);
    color: var(--success-txt);
  }

  &[data-success='false'] {
    background: var(--error-bg);
    border: 1px solid var(--error-border);
    color: var(--error-txt);
  }
`;

const IconWrapper = styled.div`
  flex-shrink: 0;
  display: grid;
  place-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  corner-shape: squircle;
  transition: background var(--color-swap-duration);

  ${Wrapper}[data-success='true'] & {
    background: var(--success-icon-bg);
  }

  ${Wrapper}[data-success='false'] & {
    background: var(--error-icon-bg);
  }
`;

export default SuccessFailIndicator;
